import type { Command } from "../../models/commands.ts";
import {
  hasAutoEmitted,
  markAutoEmitted,
  type AutoNotificationGate,
} from "../../models/notifications.ts";
import type { WorldState } from "../../models/worldState.ts";
import {
  dismissNotification,
  isValidPushNotificationInput,
  pushNotification,
  resolveDecision,
  selectNext,
  selectPrevious,
  type PushNotificationInput,
} from "../notifications/notificationStore.ts";
import { rejectCommand } from "../rejectCommand.ts";
import type { ReduceResult } from "../result.ts";

/** Host lifecycle pushes that must stay once-per-run without prefs. */
function onceGateForPush(
  command: Extract<Command, { type: "PushNotification" }>,
): AutoNotificationGate | null {
  if (command.localizedKind === "tutorialSettings") {
    return "tutorialSettings";
  }
  if (command.localizedKind === "tutorialTileFood") {
    return "tutorialTileFood";
  }
  return null;
}

export function reducePushNotification(
  state: WorldState,
  command: Extract<Command, { type: "PushNotification" }>,
): ReduceResult {
  const input: PushNotificationInput = {
    title: command.title,
    body: command.body,
    messages: command.messages,
    localizedKind: command.localizedKind,
    localizedKinds: command.localizedKinds,
    requiresDecision: command.requiresDecision,
  };

  if (!isValidPushNotificationInput(input)) {
    return rejectCommand(state, command, "invalid-notification");
  }

  const gate = onceGateForPush(command);
  if (gate && hasAutoEmitted(state.notifications, gate)) {
    return { state, events: [] };
  }

  const pushed = pushNotification(state.notifications, input);
  const localizedKind = input.localizedKind ?? input.localizedKinds?.[0];
  const notifications =
    gate === null ? pushed.state : markAutoEmitted(pushed.state, gate);

  return {
    state: {
      ...state,
      notifications,
    },
    events: [
      {
        type: "NotificationPushed",
        id: pushed.id,
        ...(localizedKind === undefined ? {} : { localizedKind }),
      },
    ],
  };
}

export function reduceDismissNotification(state: WorldState, id: string): ReduceResult {
  const notifications = dismissNotification(state.notifications, id);
  if (notifications === state.notifications) {
    return { state, events: [] };
  }
  return {
    state: { ...state, notifications },
    events: [{ type: "NotificationDismissed", id }],
  };
}

export function reduceResolveNotificationDecision(
  state: WorldState,
  id: string,
  choice: "ok" | "cancel",
): ReduceResult {
  const notifications = resolveDecision(state.notifications, id);
  if (notifications === state.notifications) {
    return { state, events: [] };
  }
  return {
    state: { ...state, notifications },
    events: [{ type: "NotificationDecisionResolved", id, choice }],
  };
}

export function reduceSelectNotificationPrevious(state: WorldState): ReduceResult {
  return {
    state: {
      ...state,
      notifications: selectPrevious(state.notifications),
    },
    events: [],
  };
}

export function reduceSelectNotificationNext(state: WorldState): ReduceResult {
  return {
    state: {
      ...state,
      notifications: selectNext(state.notifications),
    },
    events: [],
  };
}
